import { VolumeRecord } from 'types/api-responses';

/** One row of the DCA Forecast table, in the order the table shows it. */
export interface DcaForecastRow {
  productionDate: string;
  monthsOnline: number;
  oilProductionVolume: number | null;
  gasProductionVolume: number | null;
  waterProductionVolume: number | null;
  oilModelForecast: number | null;
  gasModelForecast: number | null;
  waterModelForecast: number | null;
  grossOil: number | null;
  grossGas: number | null;
  grossWater: number | null;
}

const STREAMS = ['oil', 'gas', 'water'] as const;

type Stream = (typeof STREAMS)[number];

type VolumesByDate = Map<string, Record<Stream, number | null>>;

/**
 * Extracts the calendar date from a date / timestamp string.
 *
 * @param {string | null | undefined} date - A date or timestamp.
 * @return {string | null} The date as `YYYY-MM-DD`, or null if there is no date.
 */
const toDate = (date: string | null | undefined): string | null => {
  if (!date) return null;
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(date);
  return match ? match[1] : null;
};

/**
 * Rounds half away from zero, same as Ruby's `Float#round`.
 *
 * @param {number | null | undefined} value - The value to round.
 * @return {number | null} The rounded value, or null if there was none.
 */
const round = (value: number | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  return value < 0 ? -Math.round(-value) : Math.round(value);
};

const indexByDate = (volumes: VolumeRecord[]): VolumesByDate => {
  const indexed: VolumesByDate = new Map();

  for (const volume of volumes) {
    const date = toDate(volume.date);
    if (date === null) continue;

    indexed.set(date, {
      oil: volume.oil ?? null,
      gas: volume.gas ?? null,
      water: volume.water ?? null,
    });
  }

  return indexed;
};

const monthsBetween = (from: string, to: string): number => {
  const [fromYear, fromMonth] = from.split('-').map(Number);
  const [toYear, toMonth_] = to.split('-').map(Number);
  return (toYear - fromYear) * 12 + (toMonth_ - fromMonth);
};

/**
 * Merges a well's reported production with a forecast into the rows the DCA
 * Forecast table shows, one row per month.
 *
 * A date gets a row if reported production covers it before the forecast
 * starts, or if the forecast covers it. Where both have a value for a date,
 * the row shows both. The gross columns show the reported volume for dates
 * within the reported production, and the forecast for dates outside it.
 *
 * This matches what `/reservoir/dca_forecasts/dca_table` returns, so the table
 * is the same whether the rows come from here or from that endpoint.
 *
 * @param {object} args - The two volume series and where the forecast starts.
 * @param {VolumeRecord[]} args.productionVolumes - Reported monthly production.
 * @param {VolumeRecord[]} args.forecastVolumes - The forecast's monthly volumes.
 * @param {string | null} args.forecastStartDate - The date the forecast takes
 *   over from reported production. Production on or after it is dropped unless
 *   the forecast covers it too. Null keeps every date.
 * @return {DcaForecastRow[]} One row per date, oldest first.
 */
export const buildDcaForecastRows = ({
  productionVolumes,
  forecastVolumes,
  forecastStartDate,
}: {
  productionVolumes: VolumeRecord[];
  forecastVolumes: VolumeRecord[];
  forecastStartDate?: string | null;
}): DcaForecastRow[] => {
  const production = indexByDate(productionVolumes);
  const forecast = indexByDate(forecastVolumes);
  const startDate = toDate(forecastStartDate);

  const productionDates = [...production.keys()].filter(
    (date) => startDate === null || date < startDate
  );

  const dates = [...new Set([...productionDates, ...forecast.keys()])].sort();

  if (dates.length === 0) return [];

  // The first and last date any stream reported production. Outside that
  // range the gross columns use the forecast.
  const reported = dates.filter((date) => {
    const volumes = production.get(date);
    return (
      volumes !== undefined &&
      STREAMS.some((stream) => volumes[stream] !== null)
    );
  });
  const window =
    reported.length === 0
      ? null
      : { first: reported[0], last: reported[reported.length - 1] };

  const grossValue = (
    date: string,
    productionValue: number | null | undefined,
    forecastValue: number | null | undefined
  ): number | null => {
    if (window === null) return round(forecastValue);
    if (date < window.first) return null;
    if (date <= window.last) {
      return productionValue === null || productionValue === undefined
        ? null
        : round(productionValue);
    }
    return round(forecastValue);
  };

  const firstDate = dates[0];

  return dates.map((date) => {
    const p = production.get(date);
    const f = forecast.get(date);

    return {
      productionDate: date,
      monthsOnline: monthsBetween(firstDate, date) + 1,
      oilProductionVolume: round(p?.oil),
      gasProductionVolume: round(p?.gas),
      waterProductionVolume: round(p?.water),
      oilModelForecast: round(f?.oil),
      gasModelForecast: round(f?.gas),
      waterModelForecast: round(f?.water),
      grossOil: grossValue(date, p?.oil, f?.oil),
      grossGas: grossValue(date, p?.gas, f?.gas),
      grossWater: grossValue(date, p?.water, f?.water),
    };
  });
};
